import {
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistance, useChimeMeeting } from "../../plugin/hooks";
import { fetchTokenByRequest } from "../../plugin/api/token";
import { useAuth, useFake } from "../../hooks";
import AssistanceCard from "./AssistanceCard";
import Timeline from "../../assets/images/Timeline.png"
import Timeline_arrow from "../../assets/images/Timeline_arrow.png"
import { useTranslation } from "react-i18next";
import { fetchAssistances } from "../../plugin/api/assistance";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { v4 as uuidv4 } from 'uuid';

/**
 * Renders the assistances card component
 * 
 * @param {Object} props - The props of the component
 * @returns The assistances card component
 */
const AssistancesCard = (props) => {
  const [hoveringIndex, setHoveringIndex] = useState(-1);
  const [ecallFilter, setEcallFilter] = useState(false);
  const [bcallFilter, setBcallFilter] = useState(false);
  const [scallFilter, setScallFilter] = useState(false);
  const { fetchBearer } = useAuth();
  const [selectAssistance, setSelectAssistance] = useState(false);
  const { onAssistanceRequestTokenChange, assistance } = useAssistance();
  const { onCallStart } = useFake();
  const { hasJoined, joinMeeting } = useChimeMeeting();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /**
   * Fetches the assistances
   * 
   * @returns The assistances
   */
  const assistancesQueryFunction = useCallback(
    async () => {
      const bearer = await fetchBearer()

      return fetchAssistances(bearer)
    },
    [fetchBearer],
  );

  const assistances = useQuery({
    queryKey: ["assistances"],
    queryFn: assistancesQueryFunction,
    refetchInterval: 5000,
  });

  /**
   * Handles the mouse enter event
   * 
   * @param {number} index - The index of the assistance
   */
  const onMouseEnter = useCallback((index) => {
    setHoveringIndex(index);
  }, []);

  /**
   * Handles the mouse leave event
   */
  const onMouseLeave = useCallback(() => {
    setHoveringIndex(-1);
  }, []);

  const assistanceItems = useMemo(
    () => assistances?.data?.Items?.sort((a, b) => a.timestamp - b.timestamp),
    [assistances?.data?.Items],
  );

  useEffect(() => {
    for (const assistanceItem in assistanceItems) {
      queryClient.invalidateQueries({ queryKey: ["vehicle", assistanceItem?.VIN] });
    }
  }, [assistanceItems, queryClient])

  /**
   * Handles the assistance select event
   * 
   * @param {string} assistanceRequestId - The assistance request id
   */
  const onAssistanceSelect = useCallback(
    async (assistanceRequestId) => {
      const bearer = await fetchBearer()
      const tokenResponse = await fetchTokenByRequest(
        assistanceRequestId,
        bearer,
      );

      onAssistanceRequestTokenChange({
        target: { value: tokenResponse?.Items?.[0]?.token_value || "" },
      });
      setSelectAssistance(true);
    },
    [onAssistanceRequestTokenChange, fetchBearer],
  );

  useEffect(() => {
    const join = async () => {
      if (assistance && selectAssistance && !hasJoined) {
        setSelectAssistance(false);
        await joinMeeting()
        onCallStart(assistance);
      }
    }
    join();
  }, [assistance, hasJoined, joinMeeting, onCallStart, selectAssistance]);

  const filteredAssistances = useMemo(() => {
    return [{
      assistance_request_id: uuidv4(),
      category: "scall",
      driver_name: "Justin Case",
      make: "Fiat",
      model: "500",
      fake: {
        occupants: 1
      },
      timestamp: 1735936055000
    }, {
      assistance_request_id: uuidv4(),
      category: "bcall",
      driver_name: "Paige Turner",
      make: "Mercedes",
      model: "S Class",
      fake: {
        occupants: 2
      },
      timestamp: 1735942556000
    }, {
      assistance_request_id: uuidv4(),
      category: "ecall",
      driver_name: "Will Beback",
      make: "Tesla",
      model: "Model 3",
      fake: {
        occupants: 1
      },
      timestamp: 1735966079000
    }, {
      assistance_request_id: uuidv4(),
      category: "ecall",
      driver_name: "Miles Perhour",
      make: "Hero",
      model: "HF Deluxe",
      fake: {
        occupants: 1
      },
      timestamp: 1735968746000
    }, {
      assistance_request_id: uuidv4(),
      category: "bcall",
      driver_name: "Terry Blowout",
      make: "Volkswagen",
      model: "ID4",
      fake: {
        occupants: 4
      },
      timestamp: 1735995107000
    }, {
      assistance_request_id: uuidv4(),
      category: "scall",
      driver_name: "Rent Your Ride",
      make: "Honda",
      model: "Goldwing",
      fake: {
        occupants: 1
      },
      timestamp: 1735997909000
    }, {
      assistance_request_id: uuidv4(),
      category: "bcall",
      driver_name: "Holiday Car",
      make: "Peugeot",
      model: "308",
      fake: {
        occupants: 5
      },
      timestamp: 1736059101000
    }, ...(assistanceItems || [])].filter((assistanceItem) => {
      if (!ecallFilter && !scallFilter && !bcallFilter) {
        return true;
      }
      if (ecallFilter && assistanceItem?.category === "ecall") {
        return true;
      }
      if (scallFilter && assistanceItem?.category === "scall") {
        return true;
      }
      if (bcallFilter && assistanceItem?.category === "bcall") {
        return true;
      }
      return false;
    }).sort((a, b) => a.timestamp - b.timestamp);
  }, [assistanceItems, ecallFilter, scallFilter, bcallFilter]);

  return (
    <Card {...props}>
      <CardBody paddingY={"16px"}>
        <HStack>
          <Button padding={"8px 24px"} fontSize={"16px"} fontWeight={500} variant={"primary"} colorScheme={"red"} isActive={ecallFilter} onClick={() => setEcallFilter((prev) => !prev)}>
            e-call
          </Button>
          <Button padding={"8px 24px"} fontSize={"16px"} fontWeight={500} variant={"primary"} colorScheme={"purple"} isActive={scallFilter} onClick={() => setScallFilter((prev) => !prev)}>
            s-call
          </Button>
          <Button padding={"8px 24px"} fontSize={"16px"} fontWeight={500} variant={"primary"} colorScheme={"yellow"} isActive={bcallFilter} onClick={() => setBcallFilter((prev) => !prev)}>
            b-call
          </Button>
        </HStack>
        <VStack position={"relative"} width={"full"} height={"full"}>
          <HStack width={"full"} height={"full"} justify={"flex-start"} zIndex={1}>
            {filteredAssistances.map((assistance, index) => (
              <AssistanceCard id={`assistance-${assistance.assistance_request_id}`} assistance={assistance} onAssistanceSelect={onAssistanceSelect} index={index} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} key={assistance?.make + assistance?.model + assistance?.timestamp} />
            ))}
          </HStack>
          <HStack width={"full"} gap={"60px"}>
            <VStack>
              <Text fontSize={"16px"} fontWeight={300} color={"white"}>{t("operator.time")}</Text>
              <Text fontSize={"16px"} fontWeight={300} color={"white"}>{t("operator.elapsed")}</Text>
            </VStack>
            <Box position={"relative"} width={"full"}>
              <Image src={Timeline} width={"full"} height={"50px"} />
              {filteredAssistances.map((assistance, index) => (
                <>
                  <Box padding={0} margin={0} position={"absolute"} height={"30vh"} width={"1px"} borderStyle={hoveringIndex === index ? "solid" : "dashed"} borderWidth={"1px"} borderLeftColor={hoveringIndex === index ? "#1F9FFF" : "white"} left={(40 + (index * (300 / 3))) + "px"} bottom={"3vh"} transition={"ease-in-out 1s"} />
                  <Box padding={0} margin={0} position={"absolute"} height={"20px"} width={"20px"} backgroundColor={hoveringIndex === index ? "#1F9FFF" : "white"} borderWidth={hoveringIndex === index ? "2px" : "0px"} borderStyle={"solid"} borderColor={"white"} borderRadius={"full"} left={(30 + (index * (300 / 3))) + "px"} top={"15px"} transition={"ease-in-out 1s"} />
                </>
              ))}
            </Box>
          </HStack>
          <Image src={Timeline_arrow} marginTop={"32px"} />
        </VStack>
      </CardBody>
    </Card>
  );
};

export default React.memo(AssistancesCard);
